<!-- RegistrierenSchluesseloch.vue -->
<template>
  <div class="keyhole-container">
    <h3>
      Dein Schlüsselloch bietet dir eine einfache Möglichkeit dich auf Loch.design anzumelden.
      Dafür musst du lediglich dein Schlüsselloch herunterladen und für die Anmeldung auf
      eine beliebige Stelle der Website ziehen.
    </h3>

    <!-- Show keyhole SVG if it's loaded -->
    <div v-if="keyholeSvg" class="keyhole-display" @click="handleKeyholeAction">
      <div class="keyhole-svg">
        <!-- Display version with proper sizing -->
        <div v-html="displaySvg"></div>
      </div>
      <div class="download-icon regler-icon">
        <svg width="100%" height="100%" viewBox="0 0 24 24" fill="none" stroke="currentColor">
          <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
          <polyline points="7 10 12 15 17 10"></polyline>
          <line x1="12" y1="15" x2="12" y2="3"></line>
        </svg>
      </div>
    </div>

    <button
      @click="handleKeyholeAction"
      :disabled="!keyholeSvg || isProcessing"
      class="center"
    >
      {{ buttonText }}
    </button>

    <div v-if="error" class="error-message">
      {{ error }}
    </div>

   
  </div>
</template>

<script>
import { ref, onMounted, computed } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { loginWithKeyhole } from '../services/auth'

export default {
  name: 'RegistrierenSchluesseloch',
  props: {
    email: {
      type: String,
      required: true
    },
    registrationToken: {
      type: String,
      required: true
    }
  },
  setup(props) {
    const router = useRouter()
    const route = useRoute()
    const keyholeSvg = ref(null)
    const isProcessing = ref(false)
    const error = ref('')
    const downloadSuccess = ref(false)
    const keyholeDownloaded = ref(false)
    const keyholeFile = ref(null)
    const buttonText = ref('Schlüsselloch herunterladen')

    // Sanitized SVG for download (original, unmodified)
    const sanitizedSvg = computed(() => {
      if (!keyholeSvg.value) return ''
      return keyholeSvg.value.trim()
    })

    // Modified SVG for display only - with viewBox for proper sizing
    const displaySvg = computed(() => {
      if (!keyholeSvg.value) return ''
      
      // Extract the existing SVG content
      const svgContent = keyholeSvg.value.trim()
      
      // Check if it already has a viewBox
      if (svgContent.includes('viewBox')) {
        // If it has a viewBox, just add width and height attributes
        return svgContent.replace('<svg', '<svg width="100%" height="100%"')
      } else {
        // If no viewBox, add one for display purposes
        return svgContent.replace('<svg', '<svg viewBox="0 0 500 500" width="100%" height="100%"')
      }
    })

    const generateKeyhole = async () => {
      try {
        const response = await fetch('https://laubloch.de/backend/keyhole_handler.php?action=generate', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            username: props.email,
            token: props.registrationToken
          })
        })

        if (!response.ok) {
          const errorData = await response.json()
          throw new Error(errorData.error || 'Fehler beim Generieren des Schlüssellochs')
        }

        const svgText = await response.text()
        if (!svgText.includes('<svg')) {
          throw new Error('Ungültiges SVG Format')
        }
        
        keyholeSvg.value = svgText
      } catch (err) {
        error.value = err.message || 'Fehler beim Generieren des Schlüssellochs'
        console.error('Keyhole generation error:', err)
      }
    }

    const downloadKeyhole = async () => {
      if (!keyholeSvg.value || isProcessing.value) return

      try {
        isProcessing.value = true
        error.value = ''
        buttonText.value = 'Warten...'

        // Create a Blob with the ORIGINAL SVG content - no viewBox modification
        const svgBlob = new Blob([sanitizedSvg.value], {
          type: 'image/svg+xml;charset=utf-8'
        })

        // Create URL for the Blob
        const url = URL.createObjectURL(svgBlob)

        // Create temporary link element
        const link = document.createElement('a')
        link.href = url
        link.download = 'schluesseloch.svg'
        document.body.appendChild(link)

        // Trigger download
        link.click()

        // Cleanup
        document.body.removeChild(link)
        URL.revokeObjectURL(url)

        // Save the SVG content for login - using original SVG
        const fileObj = new File([svgBlob], 'lochdesign-schluesseloch.svg', {
          type: 'image/svg+xml'
        })
        keyholeFile.value = fileObj

        // Wait at least 0.5 seconds before changing state
        await new Promise(resolve => setTimeout(resolve, 500))
        
        downloadSuccess.value = true
        keyholeDownloaded.value = true
        buttonText.value = 'Jetzt anmelden'
      } catch (err) {
        error.value = 'Fehler beim Herunterladen des Schlüssellochs'
        console.error('Download error:', err)
        buttonText.value = 'Schlüsselloch herunterladen'
      } finally {
        isProcessing.value = false
      }
    }

    const loginWithDownloadedKeyhole = async () => {
      if (!keyholeFile.value || isProcessing.value) return

      try {
        isProcessing.value = true
        error.value = ''
        buttonText.value = 'Anmelden...'

        const response = await loginWithKeyhole(keyholeFile.value)
        
        if (response.success) {
          document.cookie = `auth_token=${response.token}; path=/; max-age=2592000` // 30 days
          
          // Redirect to the original requested URL or AccountPage
          const redirect = route.query.redirect || '/AccountPage'
          router.push(redirect)
        }
      } catch (err) {
        error.value = err.message || 'Login fehlgeschlagen'
        console.error('Login error:', err)
        buttonText.value = 'Jetzt anmelden'
      } finally {
        isProcessing.value = false
      }
    }

    const handleKeyholeAction = () => {
      if (keyholeDownloaded.value) {
        loginWithDownloadedKeyhole()
      } else {
        downloadKeyhole()
      }
    }

    onMounted(() => {
      generateKeyhole()
    })

    return {
      keyholeSvg,
      sanitizedSvg,
      displaySvg,
      isProcessing,
      error,
      downloadSuccess,
      buttonText,
      handleKeyholeAction
    }
  }
}
</script>


